// Media Library — styled primitives (WL-023). Every value derives from the
// WL-001 token set (src/theme/tokens.js) + the app palette (utils/theme.js) so
// the surface composes from ONE conformant source, never magic literals.
import { styled } from "@mui/system";
import MuiSkeleton from "@mui/material/Skeleton";
import { colors } from "../../utils";
import tokens from "../../theme/tokens";

const { spacing, radius, shadow, fontFamily, fontWeight, motion, hoverHighlight } = tokens;

// Deck palette anchors (all trace to tokens/utils; named here for readability).
export const C = {
  ink: colors.primary, // #123051
  purple: colors.PrimaryPurple, // #9B5AF8
  purpleDeep: "#8F38EF", // colors.PrimaryPurple600
  purpleText: "#5A1B97", // modal title
  highlight: colors.PrimaryPurple100, // #EFE9FE
  tabIdle: "#F6F7F9", // NeutralBackground
  border: "#BBBBBB",
  borderLight: colors.lightBorder, // #e1e1e1
  track: "#E0E0E0",
  metaGrey: "#565556",
  subGrey: "#666666",
  pageBg: "#EFEFEF",
};

// nem-medialib-searchwidget-overlaps-grid: CommandPaletteRoot's global fixed
// Search/CtrlK launcher pill (src/components/CommandPalette/index.jsx —
// bottom: spacing.lg (16px), min-height: 44px) reaches 60px up from the
// viewport bottom edge. Reserve clearance below the grid content (+buffer)
// so the last row is never obscured by the pill. The pill itself is a global
// affordance and is left untouched (same technique as Notifications'
// FAB_CLEARANCE_PX / GetAppButton's reservedSpace.js).
const SEARCH_PILL_CLEARANCE_PX = 44 + 16 + 16; // pill height + bottom offset + buffer

export const Page = styled("div")({
  fontFamily,
  background: C.pageBg,
  minHeight: "100%",
  padding: `${spacing.xl}px`,
  paddingBottom: SEARCH_PILL_CLEARANCE_PX,
  boxSizing: "border-box",
});

export const TitleRow = styled("div")({
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
  gap: spacing.lg,
  marginBottom: spacing.lg,
  flexWrap: "wrap",
});

export const Title = styled("h1")({
  margin: 0,
  fontSize: 28,
  lineHeight: 1.2,
  fontWeight: fontWeight.bold,
  color: C.ink,
});

// Storage strip (mirrors Storage page bar): value + track/fill.
export const StorageWrap = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: spacing.sm,
  minWidth: 220,
  flex: "0 1 360px",
});
export const StorageLabel = styled("div")({
  fontSize: 15,
  color: C.metaGrey,
  "& b": { color: C.ink, fontWeight: fontWeight.bold },
});

// build-kelly-media-library-upgrade-cta (Kelly Ellerbrake, Slack #devs
// 2026-09-01): the caption row that used to hold ONLY `StorageLabel` now
// also carries the explicit Upgrade button, side by side — still right-
// aligned as a unit to match `StorageWrap`'s existing `align-items: flex-end`.
export const StorageCaptionRow = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: spacing.sm,
  width: "100%",
});

// Compact pill, sized for the caption row (not the 40px modal-action floor
// GhostButton/PrimaryButton use — this sits inline with 15px caption text).
export const StorageUpgradeButton = styled("button")({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 26,
  padding: "0 12px",
  boxSizing: "border-box",
  whiteSpace: "nowrap",
  lineHeight: 1,
  borderRadius: radius.pill,
  border: `1px solid ${C.purple}`,
  background: "#FFFFFF",
  color: C.purpleDeep,
  fontFamily,
  fontSize: 13,
  fontWeight: fontWeight.medium,
  cursor: "pointer",
  transition: motion.transition("background", motion.duration.fast),
  "&:hover": { background: C.highlight },
  "&:focus-visible": { outline: "none", boxShadow: shadow.focus },
});
export const StorageTrack = styled("div")({
  width: "100%",
  height: 8,
  borderRadius: radius.md,
  background: C.track,
  overflow: "hidden",
});
export const StorageFill = styled("div")(({ width }) => ({
  width: `${width}%`,
  height: "100%",
  borderRadius: radius.md,
  background: C.purple,
  transition: motion.transition("width", motion.duration.slow),
}));

// gordon-0903-media-storage-tier-bar — ONE bar divided into plan tiers.
// The track is scaled to the LARGEST plan cap, so every plan's ceiling is a
// visible tick; the fill shows what the viewer actually uses. Segments ABOVE
// the viewer's plan are the upgrade affordance (real <button>s, so they are
// keyboard-reachable and screen-reader nameable — the row is an upsell, and an
// upsell only a mouse can reach is a broken upsell).
// Wraps TierTrack (the compact, rounded, overflow:hidden VISUAL bar) so the
// touch-target overlay (TierHitTarget, below) has a relative ancestor to
// position against WITHOUT itself being clipped by TierTrack's own
// `overflow: hidden` — that clipping only needs to apply to the 10px visual
// bar (so its rounded corners hide the square segment edges), never to the
// larger invisible click surface layered on top of it.
export const TierTrackWrap = styled("div")({
  position: "relative",
  width: "100%",
});

export const TierTrack = styled("div")({
  position: "relative",
  width: "100%",
  height: 10,
  borderRadius: radius.md,
  background: C.track,
  overflow: "hidden",
  display: "flex",
});

export const TierSegment = styled("div")(({ $locked }) => ({
  position: "relative",
  height: "100%",
  // The tier LINE is NOT drawn here any more — see TierCapLine below. Nor is
  // the viewer's current tier: an orphaned inset box-shadow left over from
  // the original PR #5111 draft (keyed off isCurrent, colored with the deep-
  // purple accent token) used to render here too, predating TierCapLine — it
  // was never wired to anything but that box-shadow, so on any paid top-tier
  // viewer (isCurrent lands on the LAST, widest segment) it painted a stray
  // purple line across most of the bar (medialib-tier-bar-purple-line-
  // boxshadow). Removed; TierCapLine already owns all boundary/positional
  // decoration on this bar.
  background: $locked
    ? // Locked tiers read as "space you could buy": a light hatch, never a
      // solid fill that could be mistaken for used space.
      `repeating-linear-gradient(135deg, ${C.tabIdle}, ${C.tabIdle} 4px, #FFFFFF 4px, #FFFFFF 8px)`
    : "transparent",
}));

// gordon-0904-fix-medialib-tier-tick-labels-clipped-and-tiny-hit-target: the
// VISUAL segment (TierSegment, above) stays a slim 10px slice of the bar so
// the pill reads correctly — but a 10px-tall click/tap surface is well under
// the app's own >=32px click-surface-touch-target ruling
// (tokens.iconOverlay.minHitTarget). This is a SEPARATE, invisible, real
// <button> per LOCKED segment (the only ones that are actionable),
// absolutely positioned over its segment's slice of TierTrackWrap and
// vertically centered on the visual bar, so the tappable area is >=32px tall
// without inflating the bar itself.
export const TierHitTarget = styled("button")({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  // `height` AND `cursor` are set inline at the call site rather than here,
  // so both are asserted the same testable way as `left`/`width` below —
  // jsdom doesn't apply emotion's generated stylesheet (see TierTick), so a
  // CSS-only height or cursor here would be an invisible regression risk.
  // gordon-0904-storage-bar-pointer-cursor: this is exactly the bug that
  // shipped once already — a native <button> with NO cursor rule at all
  // defaults to the arrow, not the hand, so an above-tier segment (which IS
  // clickable — it opens the upgrade modal) silently lost its pointer
  // affordance with no test catching it.
  margin: 0,
  padding: 0,
  border: "none",
  background: "transparent",
  borderRadius: radius.md,
  transition: motion.transition("background", motion.duration.fast),
  "&:hover": { background: C.highlight },
  "&:focus-visible": { outline: "none", boxShadow: shadow.focus },
});

// gordon-0904-tier-gap-hard-to-see (Gordon: "the gap between the Free and
// Essential tiers is very hard to see"): the cap line used to be a 1px
// `border-right: C.border (#BBBBBB)` on the SEGMENT itself — i.e. UNDER the
// usage fill, which is absolutely positioned over the whole track and paints
// last. Any user whose usage passes a cap therefore lost that boundary
// entirely, and the Free/Essential caps are the two the fill reaches first.
// The line is now its own layer drawn AFTER (above) the fill, 2px wide, in
// C.ink (#123051) — measured >= 3:1 non-text contrast against BOTH surfaces
// it can sit on: 3.32:1 over the purple fill (#9B5AF8) and 10.1:1 over the
// empty track (#E0E0E0). One colour that reads on either side of the fill
// edge, so the boundary never depends on how full the bar is.
export const TIER_CAP_LINE_PX = 2;

export const TierCapLine = styled("div")({
  position: "absolute",
  top: 0,
  height: "100%",
  width: TIER_CAP_LINE_PX,
  background: C.ink,
  // Decoration only: the upgrade affordance is TierHitTarget, above.
  pointerEvents: "none",
});

// The usage fill sits ON TOP of the segments, spanning the whole track, so the
// tier boundaries stay visible through/under it. `$over` mirrors Settings/
// StorageMeterCard's red-on-overquota treatment (colors.error) — one visual
// severity language shared by both surfaces instead of two.
export const TierFill = styled("div")(({ width, $over }) => ({
  position: "absolute",
  left: 0,
  top: 0,
  width: `${width}%`,
  height: "100%",
  borderRadius: radius.md,
  background: $over ? colors.error : C.purple,
  pointerEvents: "none",
  transition: motion.transition("width", motion.duration.slow),
}));

// gordon-kelly-storage-bar-tier-mismatch: same copy/severity as Storage/
// StorageMeterCard's <S.OverBody> — Settings and Media Library must read as
// one fact about the account, not two independently-computed guesses.
export const StorageOverCaption = styled("div")({
  fontSize: 13,
  color: colors.error,
  textAlign: "right",
  marginTop: 4,
});

// gordon-0904-fix-medialib-tier-tick-labels-clipped-and-tiny-hit-target: ticks
// are positioned individually (not flexed into a same-width box as their
// segment) so a CRAMPED tick — one whose segment is narrower than its own
// label — can grow left into the track's empty space, or stagger onto a
// second row, instead of being silently clipped; see `tickRow`/`cramped` in
// storageTierBar.js. `height` only grows to fit a second row when the bar
// actually stacked one, so the common (single-row) case carries no dead
// space.
export const TierScale = styled("div")(({ $stacked }) => ({
  position: "relative",
  width: "100%",
  marginTop: 4,
  height: $stacked ? 28 : 14,
}));

export const TierTick = styled("span")(({ $locked }) => ({
  position: "absolute",
  // `top` is set inline at the call site (from the same per-segment
  // `tickRow`) rather than here — jsdom doesn't apply emotion's generated
  // stylesheet (see the hit-target height note above), so a CSS-only offset
  // here would be an invisible regression risk for the exact stagger this
  // fixes.
  fontSize: 11,
  lineHeight: 1.2,
  color: $locked ? C.subGrey : C.metaGrey,
  textAlign: "right",
  whiteSpace: "nowrap",
  // No `overflow: hidden` / fixed `width` here — that was the clipping bug.
  // The label is anchored by its RIGHT edge (the tier's own cap line, set by
  // the caller via inline `right`), so it grows LEFT into empty track space
  // instead of being cut off at a box no wider than the segment itself.
}));

// White surface card holding folders + tabs + grid.
export const Card = styled("div")({
  background: "#FFFFFF",
  borderRadius: radius.xl,
  boxShadow: shadow.e1,
  padding: `${spacing.xl}px`,
  boxSizing: "border-box",
});

export const SectionLabel = styled("div")({
  fontSize: 18,
  fontWeight: fontWeight.bold,
  color: C.ink,
  marginBottom: spacing.md,
  // gordon-a11y-medialib-contrast: was a raw #9AA0A6 literal -> 2.64:1 on white
  // (fails AA 4.5:1). C.subGrey (#666666) clears 5.75:1, same grey family.
  "& span": { color: C.subGrey, fontWeight: fontWeight.medium, marginLeft: 6 },
});

// 5-column grid template shared by folders row + media grid so edges line up.
export const gridTemplate = (cols) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
  gap: spacing.lg,
});

export const FoldersRow = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.lg,
  marginBottom: spacing.lg,
});

export const FolderCard = styled("button")({
  flex: "1 1 180px",
  maxWidth: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.sm,
  padding: "12px 14px",
  borderRadius: radius.md,
  border: `1px solid ${C.border}`,
  background: "#FFFFFF",
  cursor: "pointer",
  textAlign: "left",
  font: "inherit",
  minHeight: 64,
  transition: motion.transition("box-shadow", motion.duration.fast),
  "&:hover": { boxShadow: shadow.e1 },
  "&:focus-visible": { outline: "none", boxShadow: shadow.focus },
});
export const FolderMeta = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
  "& .name": {
    fontSize: 14,
    fontWeight: fontWeight.bold,
    color: C.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& .count": { fontSize: 12, color: C.subGrey },
});

export const NewFolderTile = styled("button")({
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: spacing.sm,
  padding: "12px 14px",
  minHeight: 64,
  borderRadius: radius.md,
  border: `1.5px dashed ${C.purple}`,
  background: "#FBFAFF",
  color: C.purpleDeep,
  fontSize: 14,
  fontWeight: fontWeight.medium,
  whiteSpace: "nowrap",
  flexWrap: "nowrap",
  cursor: "pointer",
  transition: motion.transition("background", motion.duration.fast),
  "&:hover": { background: C.highlight },
  "&:focus-visible": { outline: "none", boxShadow: shadow.focus },
  // EW-1282: disabled = no backend to create/store a media folder yet (there is
  // no media-folder API — /api/v2/folders/ is the Gallery/Wheel-listing folders
  // system, a different model). Never let this look clickable-but-inert.
  "&:disabled": { opacity: 0.5, cursor: "not-allowed", "&:hover": { background: "#FBFAFF" } },
});

// Controls row: type tabs (left) + search/sort/toggle (right).
export const ControlsRow = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.lg,
  paddingBottom: spacing.md,
  borderBottom: `1px solid ${C.borderLight}`,
  marginBottom: spacing.lg,
  flexWrap: "wrap",
});

export const Tabs = styled("div")({
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
});
export const Tab = styled("button")(({ active }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.sm,
  padding: "8px 16px",
  minHeight: 40,
  borderRadius: radius.sm,
  border: "none",
  cursor: "pointer",
  fontFamily,
  fontSize: 14,
  fontWeight: active ? fontWeight.bold : fontWeight.medium,
  // gordon-a11y-medialib-contrast: C.purple (#9B5AF8) on C.highlight (#EFE9FE)
  // bold/14px is 3.4:1 (fails AA 4.5:1). C.purpleText (#5A1B97, already used for
  // the modal title on the same tint) clears 8.7:1 and still reads as brand purple.
  color: active ? C.purpleText : C.subGrey,
  background: active ? C.highlight : C.tabIdle,
  borderTop: active ? `4px solid ${C.purple}` : "4px solid transparent",
  transition: motion.transition("background", motion.duration.fast),
  "&:hover": { background: active ? C.highlight : "#EEF0F3" },
  "&:focus-visible": { outline: "none", boxShadow: shadow.focus },
}));

export const RightControls = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: spacing.md,
  flexWrap: "wrap",
});

export const SearchBox = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: spacing.sm,
  height: 40,
  padding: "0 12px",
  minWidth: 240,
  borderRadius: radius.sm,
  border: `1px solid ${C.borderLight}`,
  background: "#FFFFFF",
  color: C.subGrey,
  "& input": {
    border: "none",
    outline: "none",
    font: "inherit",
    fontSize: 14,
    color: C.ink,
    width: "100%",
    background: "transparent",
    // ISP-9999 (gordon-0901-media-search-double-focus-outline — follow-up to
    // gordon-0831, which misdiagnosed this). MEASURED live 2026-09-01 on
    // bundle main.398b5163.js, headed Chromium, real click-focus,
    // getComputedStyle + a real document.styleSheets/`Element.matches` cascade
    // read (the same technique SearchField.doubleFocusOutline.test.js uses):
    // the gordon-0831 fix pinned the WRAPPER (below) to its neutral resting
    // border, reasoning the bare `& input { outline: none }` above already
    // "stayed the one focus indicator" — but that unconditional `outline:
    // none` has NO `:focus-visible` pseudo-class at all, so it never even
    // enters the focus-state cascade. src/styles/focusRing.css's
    // `input:focus-visible:focus-visible` rule (specificity 0,2,1 — doubled
    // pseudo-class BY DESIGN, see that file's header, so a bare
    // `outline:none` can never silently opt an <input> out) wins by a wide
    // margin and paints its purple 2px outline (offset 2px) + white 2px
    // box-shadow band directly on this <input> alone. Because this input is
    // NARROWER than the SearchBox pill (excludes the search-icon gutter), that
    // ring renders as a small purple rectangle floating disconnected inside
    // the permanently-gray pill — screenshot-confirmed as two nested purple
    // rounded rectangles with a white gap, i.e. Gordon's exact "double line"
    // report, still reproducing after gordon-0831 shipped.
    //
    // Fix: opt THIS input out using the SAME doubled-pseudo-class technique
    // the global rule itself uses (so the specificity math is symmetric, not
    // a hopeful single-pseudo guess) — identical to the header's global
    // "Search iYuga" field (`.fygDPn:focus-visible:focus-visible { outline:
    // none; box-shadow: none; }`) and let the WRAPPER below carry the one
    // ring instead, exactly like that field's own wrapper does.
    "&:focus-visible:focus-visible": { outline: "none", boxShadow: "none" },
  },
  // ISP-9999 (gordon-0923-P1-REPEAT-kelly-field-focus-style-one-shared-
  // border-only-app-wide) — supersedes the gordon-0901 fix above. `shadow.
  // focus` (the outline+box-shadow double-band ring, correct for buttons/
  // tabs/chrome CONTROLS — FolderCard/Tab/SortButton/UploadButton/etc. in
  // this same file keep it) is no longer the right token for a data-entry
  // FIELD: Gordon's 2026-09-23 rule is border-color recolor ONLY, no
  // outline, no box-shadow — matching the Edit Wheel "Wheel Name" Froala
  // reference field. This wrapper already carries a real resting border
  // (`border: 1px solid ${C.borderLight}` above), so the swap is a pure
  // color change with no layout jump.
  "&:focus-within": { outline: "none", boxShadow: "none", borderColor: "var(--iy-field-focus-color)" },
});

export const SortButton = styled("button")({
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.sm,
  height: 40,
  padding: "0 12px",
  borderRadius: radius.sm,
  border: `1px solid ${C.borderLight}`,
  background: "#FFFFFF",
  color: C.ink,
  fontFamily,
  fontSize: 14,
  cursor: "pointer",
  "&:focus-visible": { outline: "none", boxShadow: shadow.focus },
});

// ML-UP-02: the toolbar's primary "Upload" action, present in every state (the
// old page put "Upload media" ONLY in the empty state, so once a user had a
// single file there was no way to add another). height/padding/radius/fontSize
// match the neighbouring SortButton exactly so the row stays level; only the fill
// differs (this is the row's one primary action). Comfortably over the 32px
// touch-target floor; uses the app's shadow.focus ring.
export const UploadButton = styled("button")({
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.sm,
  height: 40,
  padding: "0 16px",
  borderRadius: radius.sm,
  border: "none",
  background: C.purpleDeep,
  color: "#FFFFFF",
  fontFamily,
  fontSize: 14,
  fontWeight: fontWeight.bold,
  cursor: "pointer",
  whiteSpace: "nowrap",
  flex: "0 0 auto",
  transition: motion.transition("background", motion.duration.fast),
  "&:hover": { background: C.purple },
  "&:focus-visible": { outline: "none", boxShadow: shadow.focus },
  "&:disabled": { opacity: 0.6, cursor: "progress" },
});

// ISP-9999 (gordon-0923-video-upload-11s-takes-very-long): rendered ONLY while
// `uploading` is true, next to UploadButton — a throttled-connection upload
// measured at 160.94s for a single 1080p clip previously had NO way out once
// started. Plain text-button styling (not a competing primary action).
export const CancelUploadButton = styled("button")({
  display: "inline-flex",
  alignItems: "center",
  height: 40,
  padding: "0 10px",
  border: "none",
  background: "transparent",
  color: C.purpleDeep,
  fontFamily,
  fontSize: 13,
  fontWeight: fontWeight.bold,
  textDecoration: "underline",
  cursor: "pointer",
  whiteSpace: "nowrap",
  flex: "0 0 auto",
  "&:hover": { color: C.purple },
  "&:focus-visible": { outline: "none", boxShadow: shadow.focus },
});

// "Show all" type-filter toggle (picker-filter spec, Gordon 2026-07-24): governs
// whether items outside acceptedTypes are hidden (default, OFF) or shown grayed-
// out/non-selectable (ON). Native checkbox (role="switch") under the hood so it
// is keyboard + screen-reader operable for free; the track/thumb are purely
// decorative (aria-hidden) and driven off the same `checked` state as the input.
export const ShowAllToggle = styled("label")({
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.sm,
  height: 40,
  padding: "0 4px",
  fontFamily,
  fontSize: 13,
  fontWeight: fontWeight.medium,
  color: C.ink,
  cursor: "pointer",
  userSelect: "none",
  "& input:focus-visible + span": { outline: "none", boxShadow: shadow.focus },
});
export const ShowAllTrack = styled("span")(({ checked }) => ({
  position: "relative",
  display: "inline-block",
  width: 34,
  height: 20,
  borderRadius: radius.pill,
  background: checked ? C.purple : C.track,
  flex: "0 0 auto",
  transition: motion.transition("background", motion.duration.fast),
  "&::after": {
    content: '""',
    position: "absolute",
    top: 2,
    left: checked ? 16 : 2,
    width: 16,
    height: 16,
    borderRadius: "50%",
    background: "#FFFFFF",
    boxShadow: shadow.e1,
    transition: motion.transition("left", motion.duration.fast),
  },
  "@media (prefers-reduced-motion: reduce)": { transition: "none", "&::after": { transition: "none" } },
}));
export const ShowAllInput = styled("input")({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  margin: 0,
  opacity: 0,
  cursor: "pointer",
});

export const ViewToggle = styled("div")({
  display: "inline-flex",
  height: 40,
  borderRadius: radius.sm,
  border: `1px solid ${C.borderLight}`,
  overflow: "hidden",
});
export const ViewSeg = styled("button")(({ active }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  border: "none",
  cursor: "pointer",
  color: active ? C.purple : C.subGrey,
  background: active ? C.highlight : "#FFFFFF",
  transition: motion.transition("background", motion.duration.fast),
  "& + &": { borderLeft: `1px solid ${C.borderLight}` },
  "&:focus-visible": { outline: "none", boxShadow: shadow.focusInset },
}));

// Selection bar (state 02) — occupies the tabs-row slot while selecting.
export const SelectionBar = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: spacing.lg,
  padding: "0 8px",
  minHeight: 40,
  "& .count": {
    fontSize: 14,
    fontWeight: fontWeight.bold,
    color: C.ink,
    flex: "0 0 auto",
    whiteSpace: "nowrap",
  },
  "& button": {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    border: "none",
    background: "transparent",
    font: "inherit",
    fontSize: 14,
    cursor: "pointer",
    color: C.purpleDeep,
    padding: "6px 4px",
    borderRadius: radius.sm,
    flex: "0 0 auto",
    whiteSpace: "nowrap",
  },
  "& button.danger": { color: "#D14343" },
  // EW-1282: disabled "Move to Folder" — no media-move backend endpoint exists.
  "& button:disabled": { color: C.subGrey, cursor: "not-allowed", opacity: 0.7 },
  "& button:focus-visible": { outline: "none", boxShadow: shadow.focus },
});

// ── Media grid + tiles ────────────────────────────────────────────────────────
export const Grid = styled("div")(({ cols }) => ({
  ...gridTemplate(cols),
  "@media (max-width: 1100px)": {
    gridTemplateColumns: `repeat(${Math.min(cols, 3)}, minmax(0,1fr))`,
  },
  "@media (max-width: 720px)": { gridTemplateColumns: "repeat(2, minmax(0,1fr))" },
  "@media (max-width: 420px)": { gridTemplateColumns: "repeat(1, minmax(0,1fr))" },
}));

export const Tile = styled("div")(({ selected, disabled }) => ({
  borderRadius: radius.md,
  border: selected ? `2px solid ${C.purpleDeep}` : `1px solid ${C.borderLight}`,
  background: "#FFFFFF",
  overflow: "hidden",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.55 : 1,
  transition: motion.transition("box-shadow", motion.duration.fast),
  "&:hover": disabled ? {} : { boxShadow: shadow.e2 },
  "&:focus-visible": { outline: "none", boxShadow: shadow.focus },
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
}));

// gordon-a11y-medialib-nested-interactive: SelectCheckbox/RenameChip must NOT be
// DOM descendants of Tile (role="button") -- axe's nested-interactive rule flags
// any focusable control inside an interactive-role ancestor. This wrapper anchors
// them as Tile's SIBLINGS instead (position:relative, same corner offsets) so
// they still land in the same visual spot without being nested in the tile's
// own button semantics.
export const TileWrapper = styled("div")({
  position: "relative",
});

// Ineligible-item reason line (DL-048 modal context filtering). Line-art info
// glyph + plain-language reason; AA-legible grey, not a color-only signal.
export const IneligibleNote = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 5,
  marginTop: 4,
  fontSize: 11,
  lineHeight: 1.3,
  color: C.metaGrey,
  "& svg": { flex: "0 0 auto", color: C.subGrey },
});

export const Thumb = styled("div")(({ gradient, small }) => ({
  position: "relative",
  width: "100%",
  aspectRatio: small ? "1 / 1" : "16 / 10",
  background: gradient,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

// QA-113 (Kelly, 2026-08-06): the reuse-picker tiles showed only a gradient + a
// type glyph and NEVER the real image, so a grid of the user's own uploads read
// as "broken/encrypted placeholders". The actual thumbnail now covers the tile;
// the gradient stays as the background (visible while loading, and the graceful
// fallback if the thumbnail URL errors — the <img> removes itself on error).
export const ThumbImg = styled("img")({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});

export const TypeChip = styled("div")({
  position: "absolute",
  top: 8,
  left: 8,
  width: 26,
  height: 26,
  borderRadius: radius.sm,
  background: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: shadow.e1,
});

export const DurationChip = styled("div")({
  position: "absolute",
  bottom: 8,
  right: 8,
  padding: "1px 6px",
  borderRadius: radius.sm,
  background: "rgba(18,25,38,0.82)",
  color: "#FFFFFF",
  fontSize: 12,
  fontWeight: fontWeight.medium,
  fontVariantNumeric: "tabular-nums",
});

// gordon-0831-media-library-rename: per-tile "Rename" trigger, top-right
// corner (SelectCheckbox already owns top-left; CheckChip only ever renders
// when no onToggleSelect is wired, which the real Media Library page always
// does, so this corner is free there).
export const RenameChip = styled("button")({
  position: "absolute",
  top: 8,
  right: 8,
  width: 26,
  height: 26,
  border: "none",
  borderRadius: radius.sm,
  background: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: shadow.e1,
  color: C.purpleDeep,
  cursor: "pointer",
  padding: 0,
});

export const CheckChip = styled("div")({
  position: "absolute",
  top: 8,
  right: 8,
  width: 24,
  height: 24,
  borderRadius: "50%",
  background: C.purpleDeep,
  color: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: shadow.e1,
});

// Capture ↔ Media Library unify (Phase 1): a small "Capture" provenance badge on
// the thumbnail of any item the BE tagged source==="capture". White pill + brand
// purple so it reads on any thumbnail gradient (same contrast approach as TypeChip);
// bottom-left so it never collides with the top TypeChip / top-right Check/Duration.
// `compact` (E2E-006b, Gordon 2026-07-24): in the dense reuse picker the icon+text
// badge is too large and covers the thumbnail. Compact = an unobtrusive icon-only
// corner chip (provenance kept via the title tooltip + the tile aria-label).
export const CaptureBadge = styled("div")(({ compact }) => ({
  position: "absolute",
  bottom: 8,
  left: 8,
  display: "inline-flex",
  alignItems: "center",
  gap: compact ? 0 : 4,
  padding: compact ? 3 : "2px 8px",
  borderRadius: compact ? "50%" : radius.pill,
  background: "#FFFFFF",
  color: C.purpleDeep,
  fontSize: 11,
  fontWeight: fontWeight.bold,
  boxShadow: shadow.e1,
  "& svg": { flex: "0 0 auto", display: "block" },
}));

export const HoverPill = styled("div")({
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(18,25,38,0.28)",
  opacity: 0,
  transition: motion.transition("opacity", motion.duration.fast),
  "& .pill": {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: radius.pill,
    background: "#FFFFFF",
    color: C.purpleDeep,
    fontSize: 13,
    fontWeight: fontWeight.bold,
    boxShadow: shadow.e2,
  },
});

export const TileCaption = styled("div")({
  padding: "10px 12px",
  "& .fn": {
    fontSize: 14,
    fontWeight: fontWeight.medium,
    color: C.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& .meta": { fontSize: 12, color: C.subGrey, marginTop: 2 },
  // Capture caption (source==="capture" only) — one clamped line under the name.
  "& .cap": {
    fontSize: 12,
    color: C.purpleDeep,
    marginTop: 3,
    fontStyle: "italic",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

// ── List/detail view ──────────────────────────────────────────────────────────
export const ListTable = styled("div")({
  width: "100%",
  overflowX: "auto",
});
const listCols = "52px minmax(160px,1fr) 110px 76px 84px 150px 40px";
export const ListHeader = styled("div")({
  display: "grid",
  gridTemplateColumns: listCols,
  gap: 12,
  alignItems: "center",
  position: "sticky",
  top: 0,
  padding: "10px 8px",
  background: C.tabIdle,
  borderRadius: radius.sm,
  fontSize: 12,
  fontWeight: fontWeight.bold,
  color: C.metaGrey,
  minWidth: 720,
  "& .num": { textAlign: "right", fontVariantNumeric: "tabular-nums" },
});
export const ListRow = styled("div")({
  display: "grid",
  gridTemplateColumns: listCols,
  gap: 12,
  alignItems: "center",
  padding: "8px 8px",
  minHeight: 44,
  borderBottom: `1px solid ${C.borderLight}`,
  cursor: "pointer",
  minWidth: 720,
  // Register bug #909: this row had no border-radius at all (square corners
  // on hover). hoverHighlight.radius is the shared "controls" radius every
  // other hover-highlighted row/menu-item now uses; static (not hover-only)
  // so the row's shape doesn't shift when the background fills in.
  borderRadius: hoverHighlight.radius,
  "&:hover": { background: "#FAFAFB" },
  "&:focus-visible": { outline: "none", boxShadow: shadow.focusInset },
  "& .name": {
    fontSize: 14,
    color: C.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& .cell": { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.ink },
  "& .num": { textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 13, color: C.ink },
  "& .muted": { color: "#9AA0A6" },
});
export const ListThumb = styled("div")(({ gradient }) => ({
  // QA-ML1: MUST be a positioned, clipped box. The shared <ThumbImg> is
  // position:absolute inset:0 (QA-113, real thumbnails). The grid <Thumb> is
  // position:relative so the image is contained to the tile; this list-view box
  // had no position, so the absolute image escaped to the nearest positioned
  // ancestor (the picker Modal/Backdrop) and rendered FULL-BLEED, burying the
  // list rows below the fold. relative + overflow:hidden clamps it to 36x24,
  // exactly like the grid tile.
  position: "relative",
  overflow: "hidden",
  width: 36,
  height: 24,
  borderRadius: radius.sm,
  background: gradient,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#FFFFFF",
}));

// ── Footer / pager ────────────────────────────────────────────────────────────
export const Footer = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.lg,
  marginTop: spacing.xl,
  flexWrap: "wrap",
  "& .count": { fontSize: 14, color: C.metaGrey },
});
export const Pager = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: spacing.lg,
  flexWrap: "wrap",
});
export const PerPage = styled("div")({
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.sm,
  "& .label": { fontSize: 14, color: C.metaGrey },
  "& .opts": { display: "inline-flex", borderRadius: radius.sm, overflow: "hidden", border: `1px solid ${C.borderLight}` },
});
export const PageOpt = styled("button")(({ active }) => ({
  minWidth: 44,
  height: 36,
  border: "none",
  cursor: "pointer",
  fontFamily,
  fontSize: 14,
  fontWeight: active ? fontWeight.bold : fontWeight.regular,
  // gordon-a11y-medialib-contrast: same on-tint fix as Tab above (3.4:1 -> 8.7:1).
  color: active ? C.purpleText : C.ink,
  background: active ? C.highlight : "#FFFFFF",
  "& + &": { borderLeft: `1px solid ${C.borderLight}` },
  "&:focus-visible": { outline: "none", boxShadow: shadow.focusInset },
}));
export const PagerNav = styled("div")({
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.md,
  "& button": {
    width: 40,
    height: 40,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: radius.sm,
    border: `1px solid ${C.borderLight}`,
    background: "#FFFFFF",
    cursor: "pointer",
    color: C.ink,
  },
  "& button:disabled": { opacity: 0.4, cursor: "not-allowed" },
  "& button:focus-visible": { outline: "none", boxShadow: shadow.focus },
  "& .page": { fontSize: 14, color: C.ink },
});

// ── Empty state ───────────────────────────────────────────────────────────────
export const EmptyWrap = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  padding: "56px 16px 64px",
  "& .h": { fontSize: 20, fontWeight: fontWeight.bold, color: C.ink, marginTop: spacing.xl },
  "& .p": { fontSize: 14, color: C.metaGrey, marginTop: spacing.sm },
});
export const UploadCta = styled("button")({
  marginTop: spacing.xl,
  padding: "12px 28px",
  borderRadius: radius.pill,
  border: "none",
  background: C.purpleDeep,
  color: "#FFFFFF",
  fontFamily,
  fontSize: 15,
  fontWeight: fontWeight.bold,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.sm,
  transition: motion.transition("background", motion.duration.fast),
  "&:hover": { background: C.purple },
  "&:focus-visible": { outline: "none", boxShadow: shadow.focus },
});

// ── Async: loading skeleton + error ───────────────────────────────────────────
// LOADER LAW — ONE placeholder language. This was a bespoke `mlShimmer` gradient
// sweep on a plain <div>: a THIRD loading treatment app-wide, alongside the brand
// Wheel (blocking loads) and MUI <Skeleton> (content-shaped list/grid placeholders,
// used in ~30 other components). It is now the real MUI Skeleton, so Media Library
// pulses in step with Gallery, Explore, Folders and the rest. Same public API — the
// call sites still pass their own `style` width/height/aspectRatio.
export const Skeleton = styled(MuiSkeleton)({
  borderRadius: radius.md,
  // MUI's "text" variant applies a vertical scale transform; these stand in for
  // real tiles and text bars whose exact box comes from the call site, so the box
  // must be honoured literally.
  transform: "none",
});
Skeleton.defaultProps = { variant: "rectangular", animation: "pulse" };
export const ErrorWrap = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  padding: "56px 16px",
  "& .h": { fontSize: 18, fontWeight: fontWeight.bold, color: C.ink, marginBottom: spacing.sm },
  "& .p": { fontSize: 14, color: C.metaGrey, marginBottom: spacing.lg },
});
export const RetryButton = styled("button")({
  padding: "10px 22px",
  borderRadius: radius.sm,
  border: `1px solid ${C.purple}`,
  background: "#FFFFFF",
  color: C.purpleDeep,
  fontFamily,
  fontSize: 14,
  fontWeight: fontWeight.bold,
  cursor: "pointer",
  "&:focus-visible": { outline: "none", boxShadow: shadow.focus },
});

// ── Modal shell (Move / Delete) + picker ──────────────────────────────────────
export const Backdrop = styled("div")({
  position: "fixed",
  inset: 0,
  background: "rgba(18,25,38,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: tokens.zIndex.modal,
  padding: spacing.lg,
});
export const Modal = styled("div")(({ w }) => ({
  width: "100%",
  maxWidth: w || 600,
  maxHeight: "90vh",
  overflow: "auto",
  background: "#FFFFFF",
  borderRadius: radius.md,
  border: `1px solid ${C.borderLight}`,
  boxShadow: shadow.e3,
  boxSizing: "border-box",
}));
export const ModalHead = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  padding: `${spacing.xl}px ${spacing.xl}px ${spacing.md}px`,
  "& .t": { fontSize: 24, fontWeight: fontWeight.medium, color: C.purpleText },
  "& .sub": { fontSize: 14, color: C.metaGrey, marginTop: 4 },
  "& .x": { border: "none", background: "transparent", cursor: "pointer", color: C.subGrey, padding: 4, borderRadius: radius.sm },
  "& .x:focus-visible": { outline: "none", boxShadow: shadow.focus },
});
export const ModalBody = styled("div")({ padding: `0 ${spacing.xl}px` });
export const ModalFoot = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: spacing.md,
  padding: spacing.xl,
  marginTop: spacing.md,
  borderTop: `1px solid ${C.borderLight}`,
});
// Modal action buttons. On-brand sizing conforms to THIS stylesheet's own
// action buttons (UploadButton / SortButton: height 40, inline-flex, centered).
// The old `display:block` + no `white-space:nowrap` let the label wrap to 3
// lines under the footer's flex width-constraint — measured 83px tall on
// web-stg (main.56e25379.js, 2026-08-18). inline-flex + fixed height + nowrap
// keeps them a single 40px-tall pill, matching every other modal action.
const modalActionBase = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  height: 40,
  padding: "0 20px",
  boxSizing: "border-box",
  whiteSpace: "nowrap",
  lineHeight: 1,
  borderRadius: radius.sm,
  fontFamily,
  fontSize: 14,
  cursor: "pointer",
};
export const GhostButton = styled("button")({
  ...modalActionBase,
  border: `1px solid ${C.purple}`,
  background: "#FFFFFF",
  color: C.purpleDeep,
  fontWeight: fontWeight.medium,
  "&:focus-visible": { outline: "none", boxShadow: shadow.focus },
});
export const PrimaryButton = styled("button")(({ danger }) => ({
  ...modalActionBase,
  border: "none",
  background: danger ? "#D14343" : C.purpleDeep,
  color: "#FFFFFF",
  fontWeight: fontWeight.bold,
  transition: motion.transition("background", motion.duration.fast),
  "&:hover": { background: danger ? "#B93636" : C.purple },
  "&:focus-visible": { outline: "none", boxShadow: shadow.focus },
}));

// Picker (states 03/06): header chips + caption chip.
export const Chip = styled("div")(({ tone }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 12px",
  borderRadius: radius.pill,
  fontSize: 13,
  fontWeight: fontWeight.medium,
  background: tone === "target" ? C.highlight : C.tabIdle,
  color: tone === "target" ? C.purpleDeep : C.metaGrey,
  "& b": { fontWeight: fontWeight.bold, color: C.ink },
}));
export const CaptionChip = styled("div")({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 12px",
  borderRadius: radius.pill,
  background: C.highlight,
  color: C.purpleDeep,
  fontSize: 13,
  fontWeight: fontWeight.medium,
});

// #960: multi-select checkbox, upper-left corner of the tile — takes the spot
// the type glyph used to occupy (the glyph now sits next to the filename).
// gordon-0831-media-checkboxes-off-design: this chip now hosts an MUI
// `Checkbox` (SVG-drawn, matches Storage/ContentModeration's multi-select
// checkboxes) instead of a bare native `<input>`, so sizing/color for the
// control itself lives on the `<Checkbox sx>` prop in MediaGrid.jsx, not
// here — this wrapper just supplies the white rounded chip behind it.
export const SelectCheckbox = styled("div")({
  position: "absolute",
  top: 8,
  left: 8,
  width: 26,
  height: 26,
  borderRadius: radius.sm,
  background: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: shadow.e1,
});

// #960: play/expand preview modal — media stage (image/video/audio/document).
export const PreviewStage = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#0B1220",
  borderRadius: radius.sm,
  overflow: "hidden",
  minHeight: 200,
  "& img, & video": {
    display: "block",
    width: "100%",
    maxHeight: "70vh",
    objectFit: "contain",
  },
  "& audio": { width: "100%", margin: spacing.xl },
});

export const PreviewMeta = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: `4px ${spacing.lg}px`,
  marginTop: spacing.md,
  fontSize: 13,
  color: C.metaGrey,
  "& b": { color: C.ink, fontWeight: fontWeight.medium },
});
